import {
  PERF_DOWNGRADE_FPS,
  PERF_SAMPLE_FRAMES,
  PERF_UPGRADE_FPS,
} from '@/core/constants'
import type { QualityTier } from '@/core/types'

/**
 * 自动画质降级状态机（纯函数 + 可测试）。
 *
 * 输入 :: 上一帧累计样本 + 当前帧 FPS。
 * 输出 :: 新样本（rolling average）+ 是否需要降/升档。
 *
 * 逻辑：连续 PERF_SAMPLE_FRAMES 帧低于阈值 → 降一档；连续高于 PERF_UPGRADE_FPS → 升一档。
 */
export interface PerfSample {
  /** 滚动窗口里 FPS 的总和。 */
  sum: number
  /** 当前样本数。 */
  count: number
  /** 连续低于降级阈值的帧数。 */
  lowStreak: number
  /** 连续高于升级阈值的帧数。 */
  highStreak: number
}

export const initialPerfSample: PerfSample = {
  sum: 0,
  count: 0,
  lowStreak: 0,
  highStreak: 0,
}

export type TierChange = 'downgrade' | 'upgrade' | 'none'

export interface PerfStep {
  sample: PerfSample
  change: TierChange
}

export function pushFrame(sample: PerfSample, fps: number, currentTier: QualityTier): PerfStep {
  const next: PerfSample = {
    sum: sample.sum + fps,
    count: Math.min(sample.count + 1, PERF_SAMPLE_FRAMES),
    lowStreak: fps < PERF_DOWNGRADE_FPS ? sample.lowStreak + 1 : 0,
    highStreak: fps > PERF_UPGRADE_FPS ? sample.highStreak + 1 : 0,
  }

  if (next.lowStreak >= PERF_SAMPLE_FRAMES && currentTier !== 'low') {
    return { sample: { ...initialPerfSample }, change: 'downgrade' }
  }
  if (next.highStreak >= PERF_SAMPLE_FRAMES && currentTier !== 'high') {
    return { sample: { ...initialPerfSample }, change: 'upgrade' }
  }
  return { sample: next, change: 'none' }
}

/** 顺次相邻档位切换。 */
export function nextTier(tier: QualityTier, change: TierChange): QualityTier {
  if (change === 'downgrade') {
    if (tier === 'high') return 'medium'
    if (tier === 'medium') return 'low'
    return 'low'
  }
  if (change === 'upgrade') {
    if (tier === 'low') return 'medium'
    if (tier === 'medium') return 'high'
    return 'high'
  }
  return tier
}
